import { useCallback, useEffect, useRef, useState } from 'react';
import { isSpaceTextType, spaceBaseType, spaceContentOf } from '@repo/shared-types';
import {
  spaceErrorMessage,
  spacesApi,
  type SpaceAccess,
  type SpaceIndexStatus,
  type SpaceListing,
  type SpaceNodeView,
} from './api';

// ─── Formatting ─────────────────────────────────────────────────────────────

/** Binary units, because quotas and the file-size limit are set in them (a 5 GiB quota reads as "5 GB", not "5.37 GB"). */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes)) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value >= 100 || Number.isInteger(value) ? Math.round(value) : value.toFixed(1)} ${units[unit]}`;
}

/** What the caller may do in a listing, as one word — the badge beside a folder's name. */
export function accessOf(listing: Pick<SpaceListing, 'space' | 'can'>): SpaceAccess {
  if (listing.space.owner) return 'owner';
  return listing.can.add ? 'editor' : 'viewer';
}

/**
 * The index state worth a chip. `NONE` — not yet handed to the index, which the spaces worker does
 * within a minute — says nothing a person can act on, so it gets none.
 */
export const INDEX_STATUS_LABEL: Record<Exclude<SpaceIndexStatus, 'NONE'>, string> = {
  PENDING: 'indexing',
  INDEXED: 'indexed',
  PARTIAL: 'partly indexed',
  UNSUPPORTED: 'not searchable',
  TOO_LARGE: 'too large to index',
  FAILED: 'index failed',
};

// ─── Content types ──────────────────────────────────────────────────────────

/** The media type before any parameters, lower-cased: `text/markdown; charset=utf-8` → `text/markdown`. */
export const baseType = spaceBaseType;
/** The server's own test, shared rather than mirrored. */
export const isTextType = isSpaceTextType;

const EXTENSION_TYPES: Record<string, string> = {
  md: 'text/markdown',
  markdown: 'text/markdown',
  txt: 'text/plain',
  csv: 'text/csv',
  tsv: 'text/tab-separated-values',
  json: 'application/json',
  yaml: 'application/yaml',
  yml: 'application/yaml',
  xml: 'application/xml',
  html: 'text/html',
  htm: 'text/html',
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  svg: 'image/svg+xml',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  zip: 'application/zip',
};

function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot + 1).toLowerCase() : '';
}

/**
 * The type a file is uploaded as. The browser's answer first; then the extension, because browsers
 * leave `.md` (and most text formats nobody registered) as an empty string, and a markdown note
 * stored as `application/octet-stream` could never be previewed or edited as text afterwards.
 */
export function contentTypeFor(file: File): string {
  return file.type || EXTENSION_TYPES[extensionOf(file.name)] || 'application/octet-stream';
}

export type PreviewKind = 'markdown' | 'text' | 'pdf' | 'image' | 'extracted' | 'none';

/**
 * How the file panel shows a file. Images and PDFs come from a signed inline URL; text and the text
 * read out of an office document through `GET …/text`. Which a file is comes from `spaceContentOf` in
 * shared-types — the rule the server's text endpoint and its indexer follow — so the panel never asks
 * for text the server will not give.
 */
export function previewKindOf(mimeType: string | null | undefined, name: string): PreviewKind {
  const type = baseType(mimeType);
  // Shown in place where a browser can: a PDF's own pages say more than its extracted text.
  if (type === 'application/pdf') return 'pdf';
  // The four the server will sign inline. SVG deliberately is not one of them: an SVG is a document
  // that can carry script, and opening one someone else uploaded is opening their page.
  if (type === 'image/png' || type === 'image/jpeg' || type === 'image/gif' || type === 'image/webp') return 'image';
  const content = spaceContentOf(name, mimeType);
  if (content.kind === 'text') return content.markdown ? 'markdown' : 'text';
  if (content.kind === 'document') return 'extracted';
  return 'none';
}

// ─── Waiting on the index ───────────────────────────────────────────────────

/** How often a view re-reads while something in it is still being indexed, and for how long at most. */
const INDEX_POLL_MS = 4_000;
const INDEX_POLL_MAX = 60;

/**
 * Calls `refresh` every few seconds while `waiting` holds — a file in view still being indexed — so its
 * chip and its text appear without a reload. Bounded: a file stuck waiting (a worker that is down) is
 * not polled for good; four minutes covers any healthy index job.
 */
export function useRefreshWhileIndexing(waiting: boolean, refresh: () => void, resetKey: string | null): void {
  // Each poll bumps `tick`, which is what schedules the next one while the view is still waiting.
  const [tick, setTick] = useState(0);
  const polls = useRef(0);
  useEffect(() => {
    polls.current = 0;
  }, [resetKey]);
  useEffect(() => {
    if (!waiting || polls.current >= INDEX_POLL_MAX) return;
    const timer = window.setTimeout(() => {
      polls.current += 1;
      refresh();
      setTick((t) => t + 1);
    }, INDEX_POLL_MS);
    return () => window.clearTimeout(timer);
  }, [waiting, refresh, tick]);
}

// ─── Downloads ──────────────────────────────────────────────────────────────

/**
 * Downloads a file (or one of its versions) through a short-lived signed URL.
 *
 * Navigating to it rather than opening a tab: the URL is served as an attachment, so the browser saves
 * it and stays on this page, and there is no blank tab left behind. It is fetched at the moment of the
 * click because it lasts five minutes — one minted when the panel opened could be stale by then.
 */
export async function downloadFile(fileId: string, version?: string): Promise<void> {
  const { url } = await spacesApi.download(fileId, version ? { version } : undefined);
  window.location.assign(url);
}

// ─── Clipboard ──────────────────────────────────────────────────────────────

/** `navigator.clipboard` exists only in a secure context; resolves false rather than throwing when it is refused. */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

// ─── One folder's listing ───────────────────────────────────────────────────

const PAGE = 100;

export interface FolderListingState {
  listing: SpaceListing | null;
  /** Every child loaded so far, across "Load more" pages. */
  children: SpaceNodeView[];
  nextCursor: string | null;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  loadMore: () => void;
  /** Re-reads the first page — after anything that changed the folder. Keeps the old rows visible meanwhile. */
  reload: () => void;
}

/**
 * The open folder: its first page, and further pages appended on demand.
 *
 * What was loaded is held together with the id it was loaded for, and handed out only while that is
 * still the folder asked for: otherwise the render right after a navigation would show the previous
 * folder's rows under the new folder's name, before any effect had a chance to clear them. A response
 * for a folder that is no longer the open one is dropped on arrival for the same reason — clicking
 * quickly through three folders must show the third, and a slow first response landing last would
 * otherwise replace it.
 */
export function useFolderListing(folderId: string | null): FolderListingState {
  const [data, setData] = useState<{ folderId: string; listing: SpaceListing; children: SpaceNodeView[]; nextCursor: string | null } | null>(null);
  const [status, setStatus] = useState<{ folderId: string | null; loading: boolean; error: string | null }>({ folderId: null, loading: false, error: null });
  const [loadingMore, setLoadingMore] = useState(false);
  const current = useRef<string | null>(folderId);
  current.current = folderId;

  const loadFirst = useCallback((id: string) => {
    setStatus({ folderId: id, loading: true, error: null });
    spacesApi
      .folder(id, { limit: PAGE })
      .then((page) => {
        if (current.current !== id) return;
        setData({ folderId: id, listing: page, children: page.children, nextCursor: page.nextCursor });
        setStatus({ folderId: id, loading: false, error: null });
      })
      .catch((err) => {
        if (current.current !== id) return;
        setStatus({ folderId: id, loading: false, error: spaceErrorMessage(err, 'Could not open this folder') });
      });
  }, []);

  useEffect(() => {
    if (folderId) loadFirst(folderId);
  }, [folderId, loadFirst]);

  /** Re-reads the first page, keeping the rows on screen until it lands. */
  const reload = useCallback(() => {
    if (current.current) loadFirst(current.current);
  }, [loadFirst]);

  const mine = data && data.folderId === folderId ? data : null;
  const nextCursor = mine?.nextCursor ?? null;

  const loadMore = useCallback(() => {
    const id = current.current;
    if (!id || !nextCursor || loadingMore) return;
    setLoadingMore(true);
    spacesApi
      .folder(id, { cursor: nextCursor, limit: PAGE })
      .then((page) => {
        if (current.current !== id) return;
        // By id, because a reload between two pages can return a row the next page also carries.
        setData((prev) => {
          if (!prev || prev.folderId !== id) return prev;
          const seen = new Set(prev.children.map((c) => c.id));
          return { ...prev, children: [...prev.children, ...page.children.filter((c) => !seen.has(c.id))], nextCursor: page.nextCursor };
        });
      })
      .catch((err) => {
        if (current.current === id) setStatus({ folderId: id, loading: false, error: spaceErrorMessage(err, 'Could not load more') });
      })
      .finally(() => setLoadingMore(false));
  }, [nextCursor, loadingMore]);

  const mineStatus = status.folderId === folderId ? status : null;
  return {
    listing: mine?.listing ?? null,
    children: mine?.children ?? [],
    nextCursor,
    // Loading until this folder's first answer, whichever way it went.
    loading: folderId !== null && (mineStatus ? mineStatus.loading : true),
    loadingMore,
    error: mineStatus?.error ?? null,
    loadMore,
    reload,
  };
}

// ─── The folder tree in the left pane ───────────────────────────────────────

/** Bounds the pages read for one folder's subfolders, so a folder holding thousands cannot stall the tree. */
const TREE_MAX_PAGES = 10;
const TREE_PAGE = 500;

export interface TreeEntry {
  folders: SpaceNodeView[] | null;
  loading: boolean;
  error: string | null;
}

export interface FolderTree {
  entries: Record<string, TreeEntry>;
  expanded: ReadonlySet<string>;
  toggle: (folderId: string) => void;
  /** Opens every one of these (the open folder's ancestors), loading what is not loaded. */
  expand: (folderIds: string[]) => void;
  /** Re-reads these folders' subfolders where the tree has them — after a create, rename, move or trash. */
  invalidate: (folderIds: Array<string | null | undefined>) => void;
}

/**
 * Subfolders per folder, loaded the first time a folder is expanded.
 *
 * Keyed by folder id alone rather than by space, because an id is unique across spaces and the same
 * cache then serves your own tree and the folders shared with you. Only folders are kept: the
 * listing endpoint returns files too, folders first, so reading stops once a page reaches a file.
 */
export function useFolderTree(): FolderTree {
  const [entries, setEntries] = useState<Record<string, TreeEntry>>({});
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const entriesRef = useRef(entries);
  entriesRef.current = entries;
  const inflight = useRef(new Set<string>());

  const load = useCallback(async (folderId: string) => {
    if (inflight.current.has(folderId)) return;
    inflight.current.add(folderId);
    setEntries((prev) => ({
      ...prev,
      [folderId]: { folders: prev[folderId]?.folders ?? null, loading: true, error: null },
    }));
    try {
      const folders: SpaceNodeView[] = [];
      let cursor: string | null = null;
      for (let page = 0; page < TREE_MAX_PAGES; page += 1) {
        const listing: SpaceListing = await spacesApi.folder(folderId, { cursor, limit: TREE_PAGE });
        folders.push(...listing.children.filter((c) => c.type === 'folder'));
        const last = listing.children[listing.children.length - 1];
        cursor = listing.nextCursor;
        if (!cursor || !last || last.type !== 'folder') break;
      }
      setEntries((prev) => ({ ...prev, [folderId]: { folders, loading: false, error: null } }));
    } catch (err) {
      setEntries((prev) => ({
        ...prev,
        [folderId]: { folders: prev[folderId]?.folders ?? null, loading: false, error: spaceErrorMessage(err, 'Could not load') },
      }));
    } finally {
      inflight.current.delete(folderId);
    }
  }, []);

  const toggle = useCallback(
    (folderId: string) => {
      setExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(folderId)) next.delete(folderId);
        else next.add(folderId);
        return next;
      });
      if (!entriesRef.current[folderId]?.folders) void load(folderId);
    },
    [load],
  );

  const expand = useCallback(
    (folderIds: string[]) => {
      setExpanded((prev) => {
        if (folderIds.every((id) => prev.has(id))) return prev;
        const next = new Set(prev);
        folderIds.forEach((id) => next.add(id));
        return next;
      });
      folderIds.forEach((id) => {
        if (!entriesRef.current[id]) void load(id);
      });
    },
    [load],
  );

  const invalidate = useCallback(
    (folderIds: Array<string | null | undefined>) => {
      for (const id of new Set(folderIds)) {
        if (id && entriesRef.current[id]) void load(id);
      }
    },
    [load],
  );

  return { entries, expanded, toggle, expand, invalidate };
}

// ─── Moving by drag ─────────────────────────────────────────────────────────

/**
 * The drag payload type for a row being moved. A custom type rather than `text/plain` so a row
 * dragged over the listing is never mistaken for files being dropped in from the desktop — the two
 * are told apart from `dataTransfer.types`, which is all a `dragover` handler may read.
 */
export const SPACE_NODE_DRAG_TYPE = 'application/x-cdr-space-node';

export function isNodeDrag(e: { dataTransfer: DataTransfer }): boolean {
  return Array.from(e.dataTransfer.types).includes(SPACE_NODE_DRAG_TYPE);
}

export function isFileDrag(e: { dataTransfer: DataTransfer }): boolean {
  return Array.from(e.dataTransfer.types).includes('Files');
}
